import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Users, CheckCircle2, ArrowRight, Mail, Moon, Sun, ClipboardList, FileText, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/hooks/useTheme';
import { loadFeatureFlags } from '@/components/utils/featureFlags';

export default function Me() {
  const { isDark, toggleTheme } = useTheme();
  const [featureFlags, setFeatureFlags] = useState({
    allow_company_registration: true,
    allow_individual_registration: true
  });

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      const flags = await loadFeatureFlags();
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" onClick={() => (window.location.href = '/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 text-sm px-4 py-2">
            <Users className="w-4 h-4 mr-2" />
            For Employees & Families
          </Badge>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            Your benefits, simplified
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            BenefitsPro is your company’s Section 125 benefits portal — built to make enrollment fast,
            clear, and secure.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            {featureFlags.allow_individual_registration && (
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg px-8 py-6"
                onClick={() => api.auth.redirectToLogin('/InviteCodeEntry')}
              >
                Accept an invite
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            <Button size="lg" variant="outline" className="text-lg px-8 py-6" onClick={() => (window.location.href = '/hr')}>
              I’m HR / Benefits Admin
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Fast enrollment</h3>
              <p className="text-muted-foreground">
                Review benefits and complete enrollment quickly with guided steps.
              </p>
            </CardContent>
          </Card>

          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Secure access</h3>
              <p className="text-muted-foreground">
                Access is controlled by your employer invite code and your authenticated account.
              </p>
            </CardContent>
          </Card>

          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Easy next steps</h3>
              <p className="text-muted-foreground">
                If you don’t have a code, ask HR — or invite your company to start.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="mt-10">
            <CardContent className="p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">No invite code?</h2>
                <p className="text-muted-foreground">
                  Ask your HR team to invite you — or have them set up BenefitsPro for your company.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button variant="outline" className="w-full md:w-auto" onClick={() => (window.location.href = '/why')}>
                  Why companies use it
                </Button>
                {featureFlags.allow_company_registration && (
                  <Button
                    className="w-full md:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                    onClick={() => (window.location.href = '/CompanySignup')}
                  >
                    Invite my company
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* How it works */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">How to get started</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                If your company uses BenefitsPro, you’ll get access through a secure invite.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">1) Get your invite code</h3>
                  <p className="text-muted-foreground">
                    Your HR team provides a code in an email or during onboarding.
                  </p>
                </CardContent>
              </Card>

              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                    <ClipboardList className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">2) Enter the code</h3>
                  <p className="text-muted-foreground">
                    Sign in, then enter your invitation code to link you to your employer.
                  </p>
                </CardContent>
              </Card>

              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">3) Review and enroll</h3>
                  <p className="text-muted-foreground">
                    Browse options, confirm selections, and complete your enrollment.
                  </p>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </section>

        {/* What you'll do */}
        <section className="mt-16 bg-muted/30 rounded-2xl p-8 md:p-12">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">What you’ll do inside the portal</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                The employee experience is designed to be clear and fast — so you can finish enrollment confidently.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="h-full">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">Enrollment checklist</h3>
                  <div className="space-y-3 text-muted-foreground">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>Confirm your profile details and eligibility information.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>Review available benefits and understand what you’re choosing.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>Complete enrollment steps and finalize selections.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="h-full">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">If something looks off</h3>
                  <div className="space-y-3 text-muted-foreground">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>If you can’t find your invite code, reach out to your HR team.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>If you’re not linked to a company yet, you’ll be guided to the invite flow.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>If you’re HR/admin, use the HR page to start setup.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Employee FAQ</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Quick answers for first-time users.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  q: 'Do I need an invitation code?',
                  a: 'Yes. The invite code links your account to your employer’s benefits program.'
                },
                {
                  q: 'What if I enter the wrong code?',
                  a: 'You’ll see an error. Double-check the code from your HR team and try again.'
                },
                {
                  q: 'Can I invite my company to use this?',
                  a: 'Yes. Use the “Invite my company” button to route your company to setup.'
                },
                {
                  q: 'Where do I sign in?',
                  a: 'Use the Sign In flow, then you’ll be guided to the invite code step if needed.'
                }
              ].map((item) => (
                <Card key={item.q} className="h-full">
                  <CardContent className="p-6">
                    <p className="font-semibold mb-2">{item.q}</p>
                    <p className="text-muted-foreground">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Final CTA */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 border-0">
              <CardContent className="p-10 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready to accept your invite?</h2>
                <p className="text-white/90 text-lg mb-6">
                  Sign in and enter your code to get started.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  {featureFlags.allow_individual_registration && (
                    <Button 
                      variant="secondary" 
                      onClick={() => api.auth.redirectToLogin('/InviteCodeEntry')}
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  <Button variant="secondary" onClick={() => (window.location.href = '/hr')}>
                    I’m HR / Admin
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </main>

      <footer className="bg-muted border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-foreground">© 2025 BenefitsPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
